import { useNavigate } from 'react-router-dom';
import BalancePlanChip from './home/BalancePlanChip';
import { useProfileQuery, useSubscriptionCurrentQuery } from '../core/queries/account';
import { getPlanLabel, resolveActivePlanKey, resolveCreditsTotal } from '../core/accountPresentation';
import { getPlatform, isIOSPlatform } from '../platform';
import { useAuthStore } from '../store/authStore';
import { useConsentStore } from '../store/consentStore';
import HeaderConsentLogo from './HeaderConsentLogo';

export function getPersistentBalanceChipTopOffset(
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  telegramPlatform = getPlatform(),
) {
  return isIOSPlatform(telegramPlatform, userAgent) ? 8 : 53;
}

export default function PersistentBalanceChip({ forceLogo = false }) {
  const navigate = useNavigate();
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const versionChanged = useConsentStore((state) => state.versionChanged);
  const hasConsentHistory = useConsentStore((state) => state.hasConsentHistory);
  const welcomeBonusCredits = useConsentStore((state) => state.welcomeBonusCredits);
  const authUser = useAuthStore((state) => state.user);
  const authCredits = useAuthStore((state) => state.credits);
  const isWelcomePromo = (
    consentRequired === true
    && versionChanged !== true
    && hasConsentHistory !== true
  );
  const canShowRealBalance = consentRequired === false || versionChanged === true;
  const profileQuery = useProfileQuery({
    retry: false,
    enabled: consentRequired === false,
  });
  const subscriptionQuery = useSubscriptionCurrentQuery({
    retry: false,
    enabled: consentRequired === false,
  });

  const profileUser = profileQuery.data?.user ?? profileQuery.data ?? authUser ?? null;
  const isAccountLoading =
    consentRequired === false && profileQuery.isLoading && !profileUser;
  const hasAccountError =
    consentRequired === false && profileQuery.isError && !profileUser;

  const balance = resolveCreditsTotal({
    credits: profileQuery.data ? null : authCredits,
    user: profileUser,
  });
  const planLabel = getPlanLabel(
    resolveActivePlanKey({
      subscription: subscriptionQuery.data?.subscription ?? null,
      user: profileUser,
    }),
  );
  const topOffset = getPersistentBalanceChipTopOffset();
  const displayedBalance = isWelcomePromo ? welcomeBonusCredits : balance;
  const displayedPlan = isWelcomePromo ? 'Free' : planLabel;
  const showBalanceChip = !forceLogo && (isWelcomePromo || canShowRealBalance);
  const handleClick = () => navigate('/profile');

  return (
    <div
      aria-hidden={consentRequired === false && hasAccountError ? true : undefined}
      className={[
        'fixed left-0 right-0 z-50 flex justify-center',
        'transition-opacity duration-150 ease-out',
        consentRequired === false && hasAccountError
          ? 'pointer-events-none opacity-0'
          : 'pointer-events-none opacity-100',
      ].join(' ')}
      style={{ top: `calc(var(--safe-top, 0px) + ${topOffset}px)` }}
    >
      {showBalanceChip ? (
        <div className="pointer-events-auto">
          <BalancePlanChip
            credits={displayedBalance}
            planLabel={displayedPlan}
            onClick={isAccountLoading ? undefined : handleClick}
            loading={canShowRealBalance && isAccountLoading}
          />
        </div>
      ) : (
        <HeaderConsentLogo />
      )}
    </div>
  );
}
